import { parseNetworkError } from '@trello/graphql-error-handling';
import {
  MutationCreateCardTemplateArgs,
  MutationCopyCardArgs,
  MutationDeleteCardArgs,
  MutationUploadCardCoverArgs,
  MutationUpdateCardCoverArgs,
  MutationUpdateCardDueCompleteArgs,
  MutationUpdateCardListArgs,
  MutationChangeCardDueDateArgs,
  MutationUpdateCardDatesArgs,
  Card,
  MutationUpdateCardRoleArgs,
  MutationUpdateCardNameArgs,
} from '../generated';
import {
  QueryInfo,
  prepareDataForApolloCache,
} from '../prepareDataForApolloCache';
import { JSONObject } from '../types';
import { token } from '@trello/session-cookie';
import { clientVersion } from '@trello/config';

export async function uploadCardCover(
  obj: object,
  { cardId, file, traceId }: MutationUploadCardCoverArgs,
  context: object,
  info: QueryInfo,
) {
  const formData = new FormData();
  formData.set('file', file);
  formData.set('setCover', 'true');
  formData.set('token', token || '');

  // We need to use XHR in order to track upload progress
  const request = new Promise<JSONObject>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.withCredentials = true;
    xhr.open('POST', `/1/cards/${cardId}/attachments`);
    if (traceId) {
      xhr.setRequestHeader('X-Trello-TraceId', traceId);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const response = JSON.parse(xhr.response);
        resolve(response);
      } else {
        reject(xhr.statusText);
      }
    };
    xhr.onerror = () => {
      reject({ status: xhr.status, statusText: xhr.statusText });
    };

    xhr.send(formData);
  });

  try {
    const attachment = await request;
    return prepareDataForApolloCache(attachment, info);
  } catch (e) {
    throw new Error(e.message);
  }
}

export async function updateCardCover(
  obj: object,
  { cardId, cover, traceId }: MutationUpdateCardCoverArgs,
  context: object,
  info: QueryInfo,
) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Trello-Client-Version': clientVersion,
  };
  if (traceId) {
    headers['X-Trello-TraceId'] = traceId;
  }
  // eslint-disable-next-line @trello/fetch-includes-client-version
  const response = await fetch(`/1/cards/${cardId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: headers,
    body: JSON.stringify({
      cover: cover || '',
      token,
    }),
  });

  if (response.status >= 200 && response.status < 300) {
    const card = await response.json();

    return prepareDataForApolloCache(card, info);
  } else {
    throw await parseNetworkError(response);
  }
}

export async function createCardTemplate(
  obj: object,
  { listId, name, closed }: MutationCreateCardTemplateArgs,
  context: object,
  info: QueryInfo,
) {
  const response = await fetch('/1/cards', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      idList: listId,
      name,
      closed: !!closed,
      isTemplate: true,
      token,
    }),
  });

  const card = await response.json();

  return prepareDataForApolloCache(card, info);
}

export async function copyCard(
  obj: object,
  { idCardSource, idList, name, keepFromSource, traceId }: MutationCopyCardArgs,
  context: object,
  info: QueryInfo,
) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Trello-Client-Version': clientVersion,
  };
  if (traceId) {
    headers['X-Trello-TraceId'] = traceId;
  }
  // eslint-disable-next-line @trello/fetch-includes-client-version
  const response = await fetch('/1/cards', {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      idCardSource,
      idList,
      name,
      keepFromSource: keepFromSource ? keepFromSource.join(',') : '',
      token,
    }),
  });

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const card = await response.json();

  return prepareDataForApolloCache(card, info);
}

export const deleteCard = async (
  obj: object,
  { idCard }: MutationDeleteCardArgs,
  context: object,
  info: QueryInfo,
) => {
  const response = await fetch(`/1/cards/${idCard}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      token,
    }),
  });

  await response.json();

  return prepareDataForApolloCache({ success: true }, info);
};

interface UpdateCardOptions {
  traceId?: string;
}

async function updateCard(
  cardId: string,
  attrs: Partial<Card>,
  options?: UpdateCardOptions,
) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Trello-Client-Version': clientVersion,
  };
  if (options?.traceId) {
    headers['X-Trello-TraceId'] = options.traceId;
  }
  // eslint-disable-next-line @trello/fetch-includes-client-version
  const response = await fetch(`/1/cards/${cardId}`, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      ...attrs,
      token,
    }),
  });

  if (response.status >= 200 && response.status < 300) {
    return response.json();
  } else {
    throw new Error(response.statusText);
  }
}

export async function updateCardName(
  obj: object,
  { idCard, name, traceId }: MutationUpdateCardNameArgs,
  context: object,
  info: QueryInfo,
) {
  try {
    const card = await updateCard(idCard, { name }, { traceId: traceId || '' });
    return prepareDataForApolloCache(card, info);
  } catch (error) {
    throw new Error(error.message);
  }
}

export async function updateCardList(
  obj: object,
  { idCard, idList, traceId }: MutationUpdateCardListArgs,
  context: object,
  info: QueryInfo,
) {
  try {
    const card = await updateCard(
      idCard,
      { idList },
      { traceId: traceId || '' },
    );
    return prepareDataForApolloCache(card, info);
  } catch (error) {
    throw new Error(error.message);
  }
}

export async function updateCardDueComplete(
  obj: object,
  { cardId, dueComplete, traceId }: MutationUpdateCardDueCompleteArgs,
  context: object,
  info: QueryInfo,
) {
  try {
    return prepareDataForApolloCache(
      await updateCard(cardId, { dueComplete }, { traceId: traceId || '' }),
      info,
    );
  } catch (error) {
    throw new Error(error.message);
  }
}

export async function changeCardDueDate(
  obj: object,
  { idCard, due, dueReminder, traceId }: MutationChangeCardDueDateArgs,
  context: object,
  info: QueryInfo,
) {
  try {
    const card = await updateCard(
      idCard,
      {
        due: due || '',
        dueReminder,
      },
      { traceId: traceId || '' },
    );
    return prepareDataForApolloCache(card, info);
  } catch (error) {
    throw new Error(error.message);
  }
}

export async function updateCardDates(
  obj: object,
  { idCard, due, start, dueReminder, traceId }: MutationUpdateCardDatesArgs,
  context: object,
  info: QueryInfo,
) {
  try {
    const card = await updateCard(
      idCard,
      {
        due: due || '',
        start: start || '',
        dueReminder,
      },
      { traceId: traceId || '' },
    );
    return prepareDataForApolloCache(card, info);
  } catch (error) {
    throw new Error(error.message);
  }
}

// Sending a null value via JSON does not work for the `cardRole`
// field, so we need to duplicate the `updateCard` logic in order
// to pass an empty string for null values (because this is
// incompatible with the Card['cardRole'] type)
export async function updateCardRole(
  obj: object,
  { idCard, cardRole }: MutationUpdateCardRoleArgs,
  context: object,
  info: QueryInfo,
) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Trello-Client-Version': clientVersion,
  };

  // eslint-disable-next-line @trello/fetch-includes-client-version
  const response = await fetch(`/1/cards/${idCard}`, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      cardRole: cardRole || '',
      token,
    }),
  });

  if (response.status >= 200 && response.status < 300) {
    return prepareDataForApolloCache(await response.json(), info);
  } else {
    throw new Error(response.statusText);
  }
}
